import customizationFields from '@/app/modules/payludd/customization/fields/customizationFields';

const { MPF_OPTIONS } = customizationFields;

/**
 * Check whether an MPF option is present in `mpfOptions`.
 *
 * @param {Array<{ name: string, value: * }>|undefined|null} mpfOptions
 * @param {string} optionName
 * @returns {boolean}
 */
export function isPayluddMpfOptionEnabled(mpfOptions, optionName) {
    if (!Array.isArray(mpfOptions) || !optionName) {
        return false;
    }
    return mpfOptions.some((o) => o && o.name === optionName);
}

// "CASHIER" can come from one or many MPF options (appToAppMode + concertProtocol* variants)
const CASHIER_MODE_OPTIONS = [
    MPF_OPTIONS.APP_TO_APP_MODE.key,
    MPF_OPTIONS.CONCERT_PROTOCOL_V3_2.key, // concertProtocolV320
    MPF_OPTIONS.CONCERT_PROTOCOL_V2.key,
    MPF_OPTIONS.CONCERT_PROTOCOL_V2_USB.key
];

const PAYMENT_RECEIPT_OPTION_ORDER = [
    MPF_OPTIONS.NATIVE_MERCHANT_RECEIPT.key,
    MPF_OPTIONS.NATIVE_CUSTOMER_RECEIPT.key,
    MPF_OPTIONS.DEMATERIALIZED_RECEIPT_QR_CODE.key
];

const ADDITIONAL_SERVICE_OPTION_ORDER = [
    MPF_OPTIONS.TIPS.key,
    MPF_OPTIONS.WAITER_NUMBER.key,
    MPF_OPTIONS.TABLE_NUMBER.key
];

const DEFAULT_CASHIER_PORT = 8888;
const DEFAULT_CASHIER_SPEED_RATE = 9600;

/**
 * @param {Array<{ name: string, value: * }>|undefined|null} mpfOptions
 * @param {string} optionName
 * @returns {{ name: string, value: * }|undefined}
 */
function getPayluddMpfOption(mpfOptions, optionName) {
    if (!Array.isArray(mpfOptions) || !optionName) {
        return undefined;
    }
    return mpfOptions.find((option) => option && option.name === optionName);
}

/**
 * Return all enabled option names, filtered by a provided list.
 * The provided list defines the display order (stable UI).
 *
 * @param {Array<{ name: string, value: * }>|undefined|null} mpfOptions
 * @param {string[]} optionNames
 * @returns {string[]}
 */
function collectEnabledOptionNames(mpfOptions, optionNames) {
    if (!Array.isArray(mpfOptions)) {
        return [];
    }
    return optionNames.filter((name) => isPayluddMpfOptionEnabled(mpfOptions, name));
}

/**
 * @param {Array<{ name: string, value: * }>|undefined|null} mpfOptions
 * @returns {Array<string|{ key: string, enabled: boolean }>}
 */
function collectAdditionalServiceItems(mpfOptions) {
    if (!Array.isArray(mpfOptions)) {
        return [];
    }
    return ADDITIONAL_SERVICE_OPTION_ORDER
        .map((optionName) => {
            const option = getPayluddMpfOption(mpfOptions, optionName);
            if (!option) {
                return null;
            }
            if (optionName === MPF_OPTIONS.TIPS.key) {
                return {
                    key: optionName,
                    enabled: option.value?.enabled === true
                };
            }
            return optionName;
        })
        .filter(Boolean);
}

/**
 * @param {Array<{ name: string, value: * }>|undefined|null} mpfOptions
 * @returns {string}
 */
export function getPayluddMpfModeSuffix(mpfOptions) {
    // Rules (from payluddSubscriptionResponseMock):
    // - AUTOMATE => automateMode
    // - MANUEL   => amountScreenDisplay
    // - DON      => donationMode
    // - CASHIER  => one or many of appToAppMode / concertProtocolV320 / concertProtocolV2 / concertProtocolV2Usb
    if (isPayluddMpfOptionEnabled(mpfOptions, MPF_OPTIONS.AUTOMATE_MODE.key)) {
        return 'AUTOMATE';
    }
    if (isPayluddMpfOptionEnabled(mpfOptions, MPF_OPTIONS.AMOUNT_SCREEN_DISPLAY.key)) {
        return 'MANUEL';
    }
    if (isPayluddMpfOptionEnabled(mpfOptions, MPF_OPTIONS.DONATIONS_MODE.key)) {
        return 'DONS';
    }
    if (CASHIER_MODE_OPTIONS.some((name) => isPayluddMpfOptionEnabled(mpfOptions, name))) {
        return 'CASHIER';
    }
    return undefined;
}

/**
 * Build the display value for the MPF mode field.
 * Non-CASHIER modes stay a simple suffix string; CASHIER includes enabled triggers and their config.
 *
 * @param {Array<{ name: string, value: * }>|undefined|null} mpfOptions
 * @returns {string|{ mode: string, enabledOptions: string[], allConfigurationsEnabled?: boolean, port?: number, speedRate?: number }|undefined}
 */
export function buildPayluddMpfModeValue(mpfOptions) {
    const modeSuffix = getPayluddMpfModeSuffix(mpfOptions);
    if (!modeSuffix) {
        return undefined;
    }

    if (modeSuffix !== 'CASHIER') {
        return modeSuffix;
    }

    const enabledOptions = CASHIER_MODE_OPTIONS.filter((name) => isPayluddMpfOptionEnabled(mpfOptions, name));
    const value = {
        mode: 'CASHIER',
        enabledOptions,
        allConfigurationsEnabled: enabledOptions.length === CASHIER_MODE_OPTIONS.length
    };

    if (isPayluddMpfOptionEnabled(mpfOptions, MPF_OPTIONS.CONCERT_PROTOCOL_V3_2.key)) {
        const option = getPayluddMpfOption(mpfOptions, MPF_OPTIONS.CONCERT_PROTOCOL_V3_2.key);
        const port = Number(option?.value?.port);
        value.port = Number.isFinite(port) ? port : DEFAULT_CASHIER_PORT;
    }

    if (isPayluddMpfOptionEnabled(mpfOptions, MPF_OPTIONS.CONCERT_PROTOCOL_V2.key)) {
        const option = getPayluddMpfOption(mpfOptions, MPF_OPTIONS.CONCERT_PROTOCOL_V2.key);
        const speedRate = Number(option?.value?.speedRate);
        value.speedRate = Number.isFinite(speedRate) ? speedRate : DEFAULT_CASHIER_SPEED_RATE;
    }

    return value;
}

/**
 * Flat object for DetailsGrid (MPF-derived rows).
 *
 * @param {object|undefined|null} posTerminal
 * @returns {{
 *   payluddMpfMode: (string|undefined),
 *   payluddMpfPaymentReceiptKeys: (string[]|undefined),
 *   payluddMpfAdditionalServiceKeys: (Array<string|{ key: string, enabled: boolean }>|undefined),
 *   payluddMpfBurgerMenuLock: (boolean|undefined)
 * }}
 */
export function buildPayluddMpfFlatData(posTerminal) {
    const payludd = posTerminal?.payludd;
    const mpfOptions = payludd?.mpfOptions;
    const hasPayludd = payludd !== undefined && payludd !== null;

    return {
        payluddMpfMode: hasPayludd ? buildPayluddMpfModeValue(mpfOptions) : undefined,
        payluddMpfPaymentReceiptKeys: hasPayludd
            ? collectEnabledOptionNames(mpfOptions, PAYMENT_RECEIPT_OPTION_ORDER)
            : undefined,
        payluddMpfAdditionalServiceKeys: hasPayludd
            ? collectAdditionalServiceItems(mpfOptions)
            : undefined,
        // Si pas d’objet payludd (ex. accordéon ouvert seulement pour PACE) : rien n’est affiché.
        // Si payludd présent : option MPF présente => Oui, absente => Non.
        payluddMpfBurgerMenuLock: hasPayludd
            ? isPayluddMpfOptionEnabled(mpfOptions, MPF_OPTIONS.BURGER_MENU_LOCK.key)
            : undefined
    };
}
